/**
 * T-66: the model, effort and speed a chat session runs with. New chat offers them; the hub turns them
 * into T3's model options (`[{ id, value }]`, as T3 stores them) for the session's thread.
 */

export const CHAT_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type ChatEffort = (typeof CHAT_EFFORTS)[number];

export const CHAT_EFFORT_LABEL: Record<ChatEffort, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
};

/** What New chat sends, and what a session was started with. */
export interface ChatModelSetting {
  instanceId: string;
  model: string;
  effort: ChatEffort;
  /** Fast Mode (Claude) or the priority service tier (Codex). */
  fast: boolean;
}

export const CHAT_DEFAULT_MODEL: ChatModelSetting = { instanceId: 'claudeAgent', model: 'claude-opus-5-5', effort: 'medium', fast: true };
/** T-109: a chat started with voice on: less thinking, so the first word comes sooner. */
export const CHAT_VOICE_DEFAULT_MODEL: ChatModelSetting = { ...CHAT_DEFAULT_MODEL, effort: 'low' };

/** Which option set a provider takes: T3's Claude and Codex drivers, or none we know of. */
export function chatProviderKind(instanceId: string): 'claude' | 'codex' | null {
  if (/claude/i.test(instanceId)) return 'claude';
  if (/codex/i.test(instanceId)) return 'codex';
  return null;
}

/** The efforts a model takes here: Codex has no Max; providers we don't know take none. */
export function chatEffortsFor(instanceId: string): readonly ChatEffort[] {
  const kind = chatProviderKind(instanceId);
  if (kind === 'claude') return CHAT_EFFORTS;
  if (kind === 'codex') return CHAT_EFFORTS.filter((e) => e !== 'max');
  return [];
}

/** Fast Mode is Opus-only on Claude; Codex's priority tier is on every model. */
export function chatFastAvailable(instanceId: string, model: string): boolean {
  const kind = chatProviderKind(instanceId);
  return kind === 'codex' || (kind === 'claude' && /opus/i.test(model));
}

/** The effort to send: the one asked for, else the nearest the model takes (Max → Extra high on Codex). */
export function fitChatEffort(instanceId: string, effort: ChatEffort): ChatEffort | null {
  const allowed = chatEffortsFor(instanceId);
  if (!allowed.length) return null;
  if (allowed.includes(effort)) return effort;
  return allowed[allowed.length - 1];
}

/** T3's model options for a setting; [] for a provider we don't know. */
export function chatModelOptions(s: ChatModelSetting): { id: string; value: string | boolean }[] {
  const kind = chatProviderKind(s.instanceId);
  const effort = fitChatEffort(s.instanceId, s.effort);
  const fast = s.fast && chatFastAvailable(s.instanceId, s.model);
  if (kind === 'claude') return [...(effort ? [{ id: 'effort', value: effort }] : []), ...(fast ? [{ id: 'fastMode', value: true }] : [])];
  if (kind === 'codex') return [...(effort ? [{ id: 'reasoningEffort', value: effort }] : []), ...(fast ? [{ id: 'serviceTier', value: 'priority' }] : [])];
  return [];
}

/** A request body's `model` as a setting; null when it isn't one. */
export function parseChatModelSetting(value: unknown): ChatModelSetting | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.instanceId !== 'string' || !v.instanceId || typeof v.model !== 'string' || !v.model) return null;
  if (typeof v.effort !== 'string' || !(CHAT_EFFORTS as readonly string[]).includes(v.effort)) return null;
  if (typeof v.fast !== 'boolean') return null;
  return { instanceId: v.instanceId, model: v.model, effort: v.effort as ChatEffort, fast: v.fast };
}

/** A session's model as T3 has it, read back: effort and speed from its options. */
export function chatSettingOf(model: { instanceId: string; model: string; options?: unknown } | null): { effort: string | null; fast: boolean } {
  const options = Array.isArray(model?.options) ? (model!.options as { id?: unknown; value?: unknown }[]) : [];
  const value = (...ids: string[]) => options.find((o) => typeof o?.id === 'string' && ids.includes(o.id))?.value;
  const effort = value('effort', 'reasoningEffort');
  return { effort: typeof effort === 'string' ? effort : null, fast: value('fastMode') === true || value('serviceTier') === 'priority' };
}

/** A T3 model option in its list form. */
export type ModelOption = { id: string; value: string | boolean | number };

/**
 * SF-T-194: a run's model options, as a hand-started run sends them: T3's list form (`[{ id, value }]`) with
 * malformed entries dropped, or the legacy object form as is; null when there are none to keep.
 */
export function cleanModelOptions(value: unknown): Record<string, unknown> | ModelOption[] | null {
  if (Array.isArray(value)) {
    const kept = value
      .filter(
        (o): o is ModelOption =>
          !!o &&
          typeof o === 'object' &&
          typeof o.id === 'string' &&
          o.id.length > 0 &&
          o.id.length <= 100 &&
          (typeof o.value === 'boolean' || (typeof o.value === 'string' && o.value.length <= 200) || (typeof o.value === 'number' && Number.isFinite(o.value)))
      )
      .slice(0, 20)
      .map((o) => ({ id: o.id, value: o.value }));
    return kept.length ? kept : null;
  }
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

/** SF-T-194: runs use fast mode on Claude Opus only (Codex's priority tier isn't decided by the factory yet). */
export function runFastAvailable(instanceId: string, model: string): boolean {
  return chatProviderKind(instanceId) === 'claude' && chatFastAvailable(instanceId, model);
}

/** SF-T-194: whether a model input asks for fast mode (Claude's fastMode, or Codex's priority tier), in either options form. */
export function modelUsesFast(model: { options?: unknown } | null | undefined): boolean {
  const options = model?.options;
  if (Array.isArray(options)) return chatSettingOf({ instanceId: '', model: '', options }).fast;
  if (options && typeof options === 'object') {
    const o = options as Record<string, unknown>;
    return o.fastMode === true || o.serviceTier === 'priority';
  }
  return false;
}

/** SF-T-194: a run's model as the run page shows it, "claude-opus-5-5, fast"; null when it has none. */
export function runModelText(model: { model?: unknown; options?: unknown } | null | undefined): string | null {
  if (!model || typeof model.model !== 'string' || !model.model) return null;
  return modelUsesFast(model) ? `${model.model}, fast` : model.model;
}
